import { useEffect, useState } from 'react';
import { platformSettings, type EmailConfigView, type EmailConfigBody } from '@/api/platformSettings';
import { PageHeader } from '@/components/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';

type Provider = 'sendgrid' | 'gmail_smtp' | 'smtp' | 'none';

export function EmailSettings() {
  const [current, setCurrent] = useState<EmailConfigView | null>(null);
  const [provider, setProvider] = useState<Provider>('none');
  const [sgApiKey, setSgApiKey] = useState('');
  const [sgFrom, setSgFrom] = useState('');
  const [gmEmail, setGmEmail] = useState('');
  const [gmPass, setGmPass] = useState('');
  // Generic SMTP fields
  const [smtpHost, setSmtpHost] = useState('');
  const [smtpPort, setSmtpPort] = useState<number>(587);
  const [smtpSecure, setSmtpSecure] = useState(false);
  const [smtpUser, setSmtpUser] = useState('');
  const [smtpPassword, setSmtpPassword] = useState('');
  const [smtpFrom, setSmtpFrom] = useState('');
  const [allowResellerUse, setAllowResellerUse] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<string | null>(null);

  // Fix ES-1: race-safe useEffect with ignore flag and .catch
  useEffect(() => {
    let ignore = false;
    platformSettings.getEmail().then((v) => {
      if (ignore) return;
      setCurrent(v);
      setProvider(v.provider ?? 'none');
      setAllowResellerUse(v.allow_reseller_use);
      const redacted = v.credentials_redacted;
      if (v.provider === 'sendgrid' && redacted) {
        setSgApiKey('');
        setSgFrom(redacted.from ?? '');
      } else if (v.provider === 'gmail_smtp' && redacted) {
        setGmEmail(redacted.email ?? '');
        setGmPass('');
      } else if (v.provider === 'smtp' && redacted) {
        setSmtpHost(redacted.host ?? '');
        setSmtpPort(Number(redacted.port) || 587);
        setSmtpSecure(redacted.secure === 'true');
        setSmtpUser(redacted.user ?? '');
        setSmtpPassword('');
        setSmtpFrom(redacted.from ?? '');
      }
    }).catch(() => { /* leave provider 'none' on error */ });
    return () => { ignore = true; };
  }, []);

  async function save() {
    setStatus(null);
    let body: EmailConfigBody;
    if (provider === 'none') {
      body = { provider: null };
    } else if (provider === 'sendgrid') {
      body = {
        provider: 'sendgrid',
        credentials: { apiKey: sgApiKey, from: sgFrom },
        allow_reseller_use: allowResellerUse,
      };
    } else if (provider === 'gmail_smtp') {
      body = {
        provider: 'gmail_smtp',
        credentials: { email: gmEmail, appPassword: gmPass },
        allow_reseller_use: allowResellerUse,
      };
    } else {
      body = {
        provider: 'smtp',
        credentials: {
          host: smtpHost,
          port: smtpPort,
          secure: smtpSecure,
          user: smtpUser,
          password: smtpPassword,
          from: smtpFrom,
        },
        allow_reseller_use: allowResellerUse,
      };
    }
    try {
      await platformSettings.putEmail(body);
      setStatus('Saved.');
      const next = await platformSettings.getEmail();
      setCurrent(next);
      setAllowResellerUse(next.allow_reseller_use);
    } catch (err) {
      setStatus(err instanceof Error ? err.message : 'Save failed.');
    }
  }

  async function test() {
    setTestResult('Testing…');
    try {
      const r = await platformSettings.testEmail();
      if (!r.verify.ok) setTestResult(`Verify failed: ${r.verify.error ?? 'unknown'}`);
      else if (r.sent?.ok) setTestResult('Test email sent — check your inbox.');
      else setTestResult('Verify ok but send did not return a message id.');
    } catch (err) {
      setTestResult(err instanceof Error ? err.message : 'Test failed.');
    }
  }

  return (
    <>
      <PageHeader
        eyebrow="Settings"
        title="Email"
        description="Configure how Ivrello sends invitations, password resets, and notifications."
      />
      <div className="max-w-2xl">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Provider</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div role="radiogroup" aria-label="Email provider" className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
              {([
                { v: 'none' as const, label: 'Disabled' },
                { v: 'sendgrid' as const, label: 'SendGrid' },
                { v: 'gmail_smtp' as const, label: 'Gmail SMTP' },
                { v: 'smtp' as const, label: 'Custom SMTP' },
              ]).map(({ v, label }) => (
                <label
                  key={v}
                  className={`cursor-pointer rounded-md border px-4 py-3 text-sm flex items-center gap-2 transition-colors ${
                    provider === v ? 'border-primary bg-primary/5 text-foreground' : 'border-border hover:bg-secondary/40'
                  }`}
                >
                  <input
                    type="radio"
                    name="p"
                    checked={provider === v}
                    onChange={() => setProvider(v)}
                    className="accent-primary"
                  />
                  <span>{label}</span>
                </label>
              ))}
            </div>

            {provider === 'sendgrid' && (
              <div className="space-y-3 pt-2 border-t border-border">
                <div className="space-y-1 pt-4">
                  <Label htmlFor="sgApiKey">API key</Label>
                  <Input id="sgApiKey" type="password" value={sgApiKey} onChange={(e) => setSgApiKey(e.target.value)} placeholder={current?.credentials_redacted?.apiKey ?? 'SG....'} />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="sgFrom">From address</Label>
                  <Input id="sgFrom" type="email" value={sgFrom} onChange={(e) => setSgFrom(e.target.value)} />
                </div>
              </div>
            )}
            {provider === 'gmail_smtp' && (
              <div className="space-y-3 pt-2 border-t border-border">
                <div className="space-y-1 pt-4">
                  <Label htmlFor="gmEmail">Gmail address</Label>
                  <Input id="gmEmail" type="email" value={gmEmail} onChange={(e) => setGmEmail(e.target.value)} />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="gmPass">App password</Label>
                  <Input id="gmPass" type="password" value={gmPass} onChange={(e) => setGmPass(e.target.value)} placeholder={current?.credentials_redacted?.appPassword ?? '16-char app password'} />
                </div>
                <p className="text-xs text-muted-foreground">
                  Use a Google app password, not your main Google account password. Generate one at myaccount.google.com → Security → 2-Step Verification → App passwords.
                </p>
              </div>
            )}
            {provider === 'smtp' && (
              <div className="space-y-3 pt-2 border-t border-border">
                <div className="grid gap-3 sm:grid-cols-2 pt-4">
                  <div className="space-y-1">
                    <Label htmlFor="smtpHost">Host</Label>
                    <Input id="smtpHost" value={smtpHost} onChange={(e) => setSmtpHost(e.target.value)} placeholder="smtp.example.com" />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="smtpPort">Port</Label>
                    <Input id="smtpPort" type="number" value={smtpPort} onChange={(e) => setSmtpPort(Number(e.target.value) || 0)} placeholder="587" />
                  </div>
                </div>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={smtpSecure}
                    onChange={(e) => setSmtpSecure(e.target.checked)}
                    className="accent-primary"
                  />
                  <span>Use implicit TLS (port 465 style). Leave off for STARTTLS on 587.</span>
                </label>
                <div className="space-y-1">
                  <Label htmlFor="smtpUser">Username</Label>
                  <Input id="smtpUser" value={smtpUser} onChange={(e) => setSmtpUser(e.target.value)} />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="smtpPassword">Password</Label>
                  <Input id="smtpPassword" type="password" value={smtpPassword} onChange={(e) => setSmtpPassword(e.target.value)} placeholder={current?.credentials_redacted?.password ?? ''} />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="smtpFrom">From address</Label>
                  <Input id="smtpFrom" type="email" value={smtpFrom} onChange={(e) => setSmtpFrom(e.target.value)} />
                </div>
                <p className="text-xs text-muted-foreground">
                  Use this option for any provider that accepts plain SMTP (Postmark, Mailgun, AWS SES via SMTP, Zoho, etc.).
                </p>
              </div>
            )}

            {provider !== 'none' && (
              <div className="pt-4 border-t border-border">
                <label className="flex items-start gap-3 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={allowResellerUse}
                    onChange={(e) => setAllowResellerUse(e.target.checked)}
                    className="mt-0.5 accent-primary"
                  />
                  <div>
                    <div className="text-sm font-medium">Allow resellers to use this sender</div>
                    <p className="text-xs text-muted-foreground mt-0.5 max-w-md">
                      When enabled, resellers who choose &ldquo;Platform sender&rdquo; in their email
                      settings will send through this provider. Outbound volume counts against
                      your plan — Phase F custom billing will reconcile per-reseller usage as a
                      billable item.
                    </p>
                  </div>
                </label>
              </div>
            )}

            <div className="flex flex-wrap gap-2 pt-2">
              <Button onClick={save}>Save</Button>
              <Button variant="outline" onClick={test} disabled={provider === 'none'}>Send test email</Button>
            </div>
            {/* Fix ES-2: role="alert" on both status paragraphs for screen reader announcements */}
            {status && <p className="text-sm" role="alert">{status}</p>}
            {testResult && <p className="text-sm" role="alert">{testResult}</p>}
          </CardContent>
        </Card>
      </div>
    </>
  );
}
